<template>
    <div class="team-tab">
    <!-- Follow-ups redesign (docs/plans/2026-10-02-followups-redesign-plan.md, Phase 4). Super
         Admins get the new branch unless they switch; offices follow STAGE.aging (src/utils/followups/uiVersion.js); the v-else
         branch below is the classic page, unchanged. The page still owns its data, its tabs (the
         same primaryTabIndex, CASL gates and first-tab decision) and its filters — the new branch
         only binds them to the shared components. -->
    <div v-if="fuUseNewUi" class="fu-ag" data-test="fu-aging">
      <followups-ui-toggle target="old" page-key="aging" @toggle="fuSetUi" />
      <followups-page-shell
        title="Aging"
        lede="Claims, pre-authorizations, and patient balances that are still waiting on money."
      >
        <template #tabs>
          <followups-tabs :tabs="fuTabs" active="aging" />
        </template>

        <div class="fu-ag__subtabs" role="tablist" aria-label="Aging" data-test="fu-aging-subtabs">
          <button
            v-for="t in fuSubTabs"
            :key="t.key"
            type="button"
            role="tab"
            class="fu-ag__subtab"
            :class="{ 'fu-ag__subtab--on': t.on }"
            :aria-selected="t.on ? 'true' : 'false'"
            :data-test="`fu-aging-subtab-${t.key}`"
            @click="fuSetTab(t.index)"
          >
            {{ t.label }}<span v-if="t.count !== null" class="fu-ag__subtab-count" data-test="fu-aging-subtab-count">{{ t.count }}</span>
          </button>
        </div>

        <EmptyState v-if="!fuTabAllowed" :restricted="true" :subject="fuTabSubject" />
        <template v-else>
          <followups-filter-cards
            v-if="fuQueueKey === 'patientBalances'"
            :cards="fuBalanceCards"
            :clickable="false"
            label="Patient balances"
          />
          <followups-filter-cards
            v-else
            :cards="fuBucketCards"
            :active="fuBucket"
            :label="fuQueueKey === 'outstandingClaims' ? 'Claims by days since sent' : 'Pre-authorizations by days since sent'"
            @select="fuBucket = $event"
          />
          <followups-queue
            :key="fuQueueKey"
            :queue="fuQueueKey"
            mode="page"
            :rows="fuRows"
            :busy="busy"
            :external-filter="!!fuBucket"
            @counts="fuCounts = $event"
            @clear-filters="fuBucket = null"
          >
            <template v-if="fuQueueKey === 'outstandingClaims'" #filters>
              <label class="fu-ag__affix">
                <span class="fu-ag__affix-text">Older than</span>
                <input
                  v-model="agingOlderThan"
                  type="text"
                  inputmode="numeric"
                  class="fu-ag__affix-input"
                  placeholder="Any"
                  aria-label="Older than, in days"
                  data-test="fu-older-than"
                  @keydown.enter.prevent="fuApplyOlder"
                />
                <span class="fu-ag__affix-text fu-ag__affix-text--end">days</span>
              </label>
              <button type="button" class="fu-ag__btn" data-test="fu-older-apply" :disabled="busy" @click="fuApplyOlder">Apply</button>
              <span class="fu-ag__hint">Blank shows the last 6 months</span>
            </template>
            <template v-else-if="fuQueueKey === 'patientBalances'" #filters>
              <label class="fu-ag__affix">
                <span class="fu-ag__affix-text">Over $</span>
                <input
                  type="text"
                  inputmode="decimal"
                  class="fu-ag__affix-input"
                  placeholder="0"
                  :value="fuBalanceOverText"
                  aria-label="Balance over, in dollars"
                  data-test="fu-balance-over"
                  @input="fuSetBalanceOver($event)"
                />
              </label>
              <select
                class="fu-ag__select"
                :value="fuBillingValue"
                aria-label="Billing type"
                data-test="fu-billing-type"
                @change="fuSetBilling($event.target.value)"
              >
                <option value="">All billing types</option>
                <option v-for="b in fuBillingOptions" :key="b.ItemName" :value="b.ItemName">{{ b.ItemName }}</option>
              </select>
              <followups-switch v-model="showNegativeBalances" label="Credits only" data-test="fu-credits-only" />
            </template>
          </followups-queue>
        </template>
      </followups-page-shell>
    </div>
    <template v-else>
    <followups-ui-toggle target="new" page-key="aging" @toggle="fuSetUi" />
        <h2 class="page-title">Aging</h2>
        <AgingSkeleton v-if="busy" />
        <b-row v-show="!busy">
          <b-col xl="12" xs="12">
            <!-- TAB PANEL START -->
            <section class="tab-panel">
              <b-tabs v-model="primaryTabIndex">
                <!-- FIRST TAB START -->
                <b-tab title="Outstanding Claims" href="#outstandingClaims" active>
                  <template v-if="canViewOutstandingClaims">
                    <div class="modern-table-panel">
                      <div class="panel-header">
                        <h5 class="section-title">
                          <i class="fa fa-file-text-o section-icon"></i>
                          Outstanding Claims
                        </h5>
                        <p class="panel-subtitle">By default, if no filters applied, Outstanding Claims within the last 6 months are shown.</p>
                      </div>
                      <div class="aging-filter-bar">
                        <div class="row d-flex justify-content-start align-items-center mx-0 px-3 py-3">
                          <b-input-group
                            prepend="Older than"
                            append="days"
                            placeholder=""
                            class="p-1 col-xl-3 col-lg-4 col-md-6 col-sm-7">
                            <b-form-input v-model="agingOlderThan" type="number" min="0" step="1"></b-form-input>
                          </b-input-group>
                          <div class="col-xl-5 col-lg-2 col-md-3 col-sm-12 px-1 text-left d-inline-block">
                            <b-button variant="success" type="submit" class="btn btn-sm" @click="applyOutstandingClaimsFilter" v-if="!loading">Apply</b-button>
                            <span class="text-muted mt-0 p-2 d-inline-block">
                              <p class="my-0"><small>Leave blank to see all</small></p>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="table-content">
                        <AdminTable v-if="this.outstandingClaimsData" :data="this.outstandingClaimsData" :columns="outStandingClaimsTableColumns" :options="options" :extra="teamioTableExtraParamsOutstandingClaims" />
                      </div>
                    </div>
                  </template>
                  <EmptyState v-else :restricted="true" subject="outstanding_claim" />
                </b-tab>
                <!-- FIRST TAB END -->
                <!-- SECOND TAB START -->
                <b-tab title="Outstanding PreAuthorizations" href="#preAuth">
                  <template v-if="canViewOutstandingPreAuths">
                    <div class="inner-tab">
                      <b-tabs v-model="preAuthTabIndex">
                        <b-tab title="Outstanding PreAuthorizations" href="#outstandingPreAuth" active>
                          <div class="mt-3">
                            <div class="modern-table-panel">
                              <div class="panel-header">
                                <h5 class="section-title">
                                  <i class="fa fa-file-text-o section-icon"></i>
                                  Outstanding PreAuthorizations
                                </h5>
                                <p class="panel-subtitle">PreAuthorizations that have been sent out but not marked as received.</p>
                              </div>
                              <div class="table-content">
                                <AdminTable v-if="this.outstandingPreAuthData" :data="this.outstandingPreAuthData" :columns="preAuthTableColumns1" :options="options" :extra="teamioTableExtraParamsOutstandingPreAuth" />
                              </div>
                            </div>
                          </div>
                        </b-tab>
                                      <!-- ⚠️ BEFORE RE-ENABLING this tab (2026-07-27 date audit): its DatePickers have no
                                           value-type, so dateFrom/dateTo would be raw Date objects, and /runquery does ZERO
                                           date normalization — a raw Date serializes to a UTC ISO timestamp and lands in
                                           date('...') SQL, shifting the day for evening-US users. Format with
                                           moment(x).format('YYYY-MM-DD') before building the query, like every live picker
                                           in this codebase (see OverdueRecall.vue applyFilter). -->
                                      <!--
                                      <b-tab title="Follow-ups for Processed PreAuthorizations" href="#processedPreAuth">
                                          <div class="row">
                                              <div class="col-sm-12">
                                              <b-card  class="background-cover">
                                                  <p class="mt-1 fw-semi-bold">By default, if no filters applied, PreAuthorizations marked as received by your team within the last 6 months are shown. These patients have not scheduled for at least one of the procedures attached to the PreAuthorization. Use this table to track patients who haven't followed through with treatment.</p>
                                                  <div class="d-flex justify-content-start align-items-center">
                                                   
                                                    
                                                    <p class="row from-to-section bg-light w-100 p-3">
                                                      <span class="from__ "><small class="fw-semi-bold">Date Received From: </small> <DatePicker
                                                            lang="en"
                                                            placeholder=""
                                                            input-class="form-control"
                                                            v-model="dateFrom"
                                                            format="MM-DD-YYYY"
                                                            class=""
                                                          >
                                                            <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                                                          </DatePicker>
                                                      </span>
                                                          &nbsp;
                                                      <span class="to__ ml-4"><small class="fw-semi-bold">Date Received To: </small> <DatePicker
                                                            lang="en"
                                                            placeholder=""
                                                            input-class="form-control"
                                                            v-model="dateTo"
                                                            format="MM-DD-YYYY"
                                                            class=""
                                                          >
                                                            <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                                                          </DatePicker>
                                                      </span>
                                                          &nbsp;
                                                      <b-button variant="success" type="submit" class="apply_f btn-sm mb-2 ml-1" @click="applyProcessedPreAuthFilter">Apply</b-button>
                                                    </p>
                                                    
                                                    
                                                  </div>
                                                    <div class="table-panel">
                                                      <AdminTable v-if="this.processedPreAuthData" :data="this.processedPreAuthData" :columns="preAuthTableColumns2" :options="options" :extra="teamioTableExtraParamsProcessedPreAuth" />
                                                    </div>
                                              </b-card>
                                              </div>
                                          </div>
                                      </b-tab>
                        -->
                      </b-tabs>
                    </div>
                  </template>
                  <EmptyState v-else :restricted="true" subject="outstanding_preauths" />
                </b-tab>
                <!-- SECOND TAB END -->
                <!-- THIRD TAB START -->
                <b-tab title="Patient Balances" href="#patientBalances">
                  <template v-if="canViewPatientBalances">
                    <div class="modern-table-panel">
                                <div class="panel-header">
                                  <h5 class="section-title">
                                    <i class="fa fa-dollar section-icon"></i>
                                    Patient Balances
                                  </h5>
                                  <p class="panel-subtitle">Guarantors who have a balance on their account, sorted by their most recent visit.</p>
                                </div>
                                <div class="aging-filter-bar">
                                  <div class="row d-flex justify-content-start align-items-center mx-0 px-3 py-3">
                                    <div class="col-lg-4">
                                      <b-input-group prepend="Filter by amounts greater than $" append=".00" class="">
                                        <b-form-input v-model="patientBalanceGreaterThan"></b-form-input>
                                      </b-input-group>
                                    </div>
                                    <div class="col-lg-5">
                                      <b-form-checkbox
                                        id="showNegativeBalanceCheckbox"
                                        v-model="showNegativeBalances"
                                        name="checkbox-1"
                                        class=""
                                        switch
                                        size="lg"
                                      >
                                        <p class="mb-0">Show patients with negative balances (credits)</p>
                                      </b-form-checkbox>
                                    </div>
                                    <div class="col-lg-3">
                                      <multiselect
                                        v-if="billingTypes"
                                        v-model="billingType"
                                        :options="billingTypes"
                                        :multiple="false"
                                        :taggable="false"
                                        :show-labels="false"
                                        label="ItemName"
                                        track-by="ItemName"
                                        placeholder="Select Billing Type"
                                        class=""
                                      />
                                    </div>
                                  </div>
                                </div>
                      <div class="table-content">
                        <AdminTable v-if="this.patientBalancesData" :data="this.patientBalancesData" :columns="patientBalanceTableColumns" :options="patientBalancesOptions" :extra="teamioTableExtraParamsPatientBalances" />
                      </div>
                    </div>
                  </template>
                  <EmptyState v-else :restricted="true" subject="patient_balances" />
                </b-tab>
                <!-- THIRD TAB END -->
              </b-tabs>
            </section>
            <!-- TAB PANEL END -->
          </b-col>
        </b-row>
    <div>
    </div>
    </template>
    <!-- The new branch's ONE slide-out outlet (the follow-up drawer) — one for all three queues.
         The legacy branch's outlets live inside its AdminTables; v-if / v-else keeps the two from
         ever being mounted together. -->
    <slideout-panel v-if="fuUseNewUi"></slideout-panel>
  </div>
</template>

<script>
/* eslint-disable */

import DatePicker from "vue2-datepicker";
import Multiselect from "vue-multiselect";
import { mapState, mapActions, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import mixin from '../../mixin.js';
import truncate from 'vue-truncate-collapsed';
import AdminTable from '@/pages/Admin/AdminTable';
import AgingSkeleton from './AgingSkeleton.vue';
import EmptyState from '@/components/EmptyState/EmptyState.vue';
// Follow-ups redesign (Phase 4) — the new branch of the template. See fuUseNewUi.
import FollowupsQueue from '@/components/Followups/FollowupsQueue.vue';
import FollowupsPageShell from '@/components/Followups/FollowupsPageShell.vue';
import FollowupsTabs from '@/components/Followups/FollowupsTabs.vue';
import FollowupsFilterCards from '@/components/Followups/FollowupsFilterCards.vue';
import FollowupsSwitch from '@/components/Followups/FollowupsSwitch.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
import { switchUi } from '@/utils/followups/uiSwitch.js';
import { visibleTabs } from '@/utils/followups/pageChrome.js';
import { agingBucketCards, rowsInAgingBucket, balanceCards } from '@/utils/followups/filterCards.js';
import { formatMoney } from '@/utils/followups/rowModel.js';

// The new branch's sub-tabs: primaryTabIndex → the queues.js queue it shows (index 1 is the
// OUTSTANDING pre-auths — the processed pre-auth tab is commented out on the old page too).
const FU_AGING_QUEUES = ['outstandingClaims', 'outstandingPreAuths', 'patientBalances'];

// Tab index → CASL feature key. Drives both the per-tab access gate and
// the watcher's query-skip logic. Keep in sync with the inlined map in
// cypress/node-tests/aging-tab-decision.test.js.
const AGING_TAB_SUBJECTS = {
  0: 'outstanding_claim',
  1: 'outstanding_preauths',
  2: 'patient_balances',
};

export default {
  name: "Aging",
  components: {
    DatePicker, Multiselect, truncate, AdminTable, AgingSkeleton, EmptyState,
    FollowupsQueue, FollowupsPageShell, FollowupsTabs, FollowupsFilterCards, FollowupsSwitch, FollowupsUiToggle,
  },
  props: ['list'],
  mixins: [mixin],

  created() {
    this.init();
  },

  mounted() {
    // Hash navigation wins — if the user deep-linked to a specific tab,
    // honor that (the per-tab restricted state inside the tab will show
    // the lock UI if they lack access). Otherwise default to the first
    // tab the user is actually allowed to see — prevents an Outsourced
    // user with FEATURE_PERMISSIONS revoking outstanding_claim from
    // landing on tab 0 (Outstanding Claims) restricted-state and having
    // to manually click another tab.
    switch(this.$route.hash) {
      case '#outstandingClaims': this.primaryTabIndex = 0; return;
      case '#preAuth': this.primaryTabIndex = 1; this.preAuthTabIndex = 0; return;
      case '#outstandingPreAuth': this.primaryTabIndex = 1; this.preAuthTabIndex = 0; return;
      case '#processedPreAuth': this.primaryTabIndex = 1; this.preAuthTabIndex = 1; return;
      case '#patientBalances': this.primaryTabIndex = 2; return;
    }
    const firstAllowed = this.firstAllowedTabIndex;
    if (firstAllowed !== 0 && firstAllowed !== this.primaryTabIndex) {
      this.primaryTabIndex = firstAllowed;
    }
  },

  data() {
    return {
      // Follow-ups redesign (new branch only) — UI state; the data, tabs and filters below stay the truth.
      fuUiVersion: 0,      // bumped by the Classic / New view links so fuUseNewUi re-reads storage
      fuCounts: null,      // the queue's chip counts (the tab badge)
      fuBucket: null,      // the age-bucket card that is filtering claims / pre-auths ('b0'…'b3')
      results: [],
      busy: false,
      billingTypes: [],
      pieChartOptions: {
        layout: {
            padding: {
              left: 0,
              right: 0,
              top: 0,
              bottom: 0
            }
        },
        legend: {
          display: true,
          position: window.innerWidth < 1400 ? 'bottom' : 'right',
          fullWidth: true,
          labels:{
            padding: 10,
            boxWidth: 15,
            labelWidth: 10
            // margin: 10
          }
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {              
              // https://www.chartjs.org/docs/latest/configuration/tooltip.html#label-callback
             var label = data.labels[tooltipItem.index] + ': $' + data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
             return label;
            }
          }

        },
        responsive: true,
        maintainAspectRatio: false
      },
      teamioTableExtraParamsOutstandingClaims: {
        detailPageName: 'AgingDetails',
      },
      teamioTableExtraParamsOutstandingPreAuth: {
        detailPageName: 'OutstandingPreAuthDetails',
      },
      teamioTableExtraParamsProcessedPreAuth: {
        detailPageName: 'ProcessedPreAuthDetails',
      },
      teamioTableExtraParamsPatientBalances: {
        detailPageName: 'PatientBalancesDetails',
      },
      primaryTabIndex: 0,
      preAuthTabIndex: 0,
      tabs: ['#outstandingClaims', '#preAuth', '#outstandingPreAuth', '#processedPreAuth', '#patientBalances'],
      agingOlderThan: '',
      preAuthOlderThan: '',
      dateFrom:'',
      dateTo:'',
      patientBalanceGreaterThan:0,
      billingType: null,
      showNegativeBalances: false,
      outstandingClaimStatusColors: {
        'Did not receive - RESEND': 'pink',
        'Call Back, Not Ready': 'primary',
        'Needs more info': 'primary',
        'Processed - check is in mail' : 'f87979',
        'No Answer': 'purple',
        'Not Called': 'orange',
        'Patient Asked Not To Be Called': 'danger',
        'Scheduled': 'success',
        'Patient Will Call Us': 'FF6384',
      },
      statusColors: {
        'Not Called': 'orange',
        'Did not receive - RESEND': 'pink',
        'Call Back, Not Ready': 'primary',
        'Needs more info': 'pink',
        'Processed - check is in mail' : 'f87979',
        'No Answer': 'purple',
        'Not Called': 'orange',
        'Patient Asked Not To Be Called': 'danger',
        'Scheduled': 'success',
        'Patient Will Call Us': 'FF6384',
      },
      //colors: ["#ff7321", "#808080", "#3165bb", "#ed1c24"],
      colors: ["#AAF0D1", "#AEE6E6", "#FFA45B", "#D51D48"],
      //outStandingClaimsTableColumns: ['Patient', 'DateSent', 'dateOfService', 'Carrier','attachedProcedures','claimFee','Status','FollowUpActionPlan', 'Attempts'],
      outStandingClaimsTableColumns: ['Patient', 'DateSent', 'dateOfService', 'Carrier','claimFee','Status','FollowUpActionPlan', 'Attempts'],
      //preAuthTableColumns1 : ['Patient', 'DateSent', 'Carrier', 'attachedProcedures','Status','FollowUpActionPlan', 'Attempts'],
      preAuthTableColumns1 : ['Patient', 'DateSent', 'Carrier', 'Status','FollowUpActionPlan', 'Attempts'],
      //preAuthTableColumns2 : ['Patient', 'DateSent', 'dateReceived','Carrier','attachedProcedures','Status','FollowUpActionPlan', 'Attempts'],
      preAuthTableColumns2 : ['Patient', 'DateSent', 'dateReceived','Carrier','Status','FollowUpActionPlan', 'Attempts'],
      patientBalanceTableColumns:['Guarantor', 'amount', 'lastVisit', 'billingType', 'Status','FollowUpActionPlan','Attempts'],
      options: {
          skin:'table',
          sortByColumn: true,
          sortable: ['Patient', 'DateSent', 'Carrier', 'claimFee', 'Status', 'Attempts'],
          perPage: 50,
          pagination: { chunk: 10, dropdown: false },
          filterByColumn: true,               // https://matanya.gitbook.io/vue-tables-2/list-filters
          filterable: ['Patient', 'Carrier', 'Status', 'Attempts'],
          texts: {
            filter: 'Filter by Patient or Carrier'
            //count:' '
          },
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          },
          sortIcon: {
            base: "fa",
            is: "fa-sort",
            up: "fa-sort-asc",  
            down: "fa-sort-desc"
          },          
          customSorting: {
              claimFee: function (ascending) {
                return function (a, b) {
                  if(ascending)
                    return a.ClaimFee - b.ClaimFee;
                  else
                    return b.ClaimFee - a.ClaimFee;
                }
              }
          }          
      },

      /*
      outstandingClaimOptions: {
        ...options,
        filterable: ['Patient', 'Carrier', 'Status']
      },
      outstandingPreAuthOptions: {
        ...options,
        filterable: ['Patient', 'Carrier', 'Status'],
      },
      */

      patientBalancesOptions: {
        skin:'table',
          sortByColumn: true,
          sortable: ['Guarantor', 'amount', 'lastVisit', 'billingType', 'Status', 'Attempts'],
          perPage: 50,
          pagination: { chunk: 10, dropdown: false },
          filterByColumn: true,               // https://matanya.gitbook.io/vue-tables-2/list-filters
          filterable: ['Guarantor', 'lastVisit', 'Status', 'Attempts'],
          headings: {
            Guarantor: 'Guarantor',
            amount: 'Amount',
            lastVisit: 'Last Visit',
            billingType: 'Billing Type',
            Status: 'Status',
            FollowUpActionPlan: 'Follow Up Action Plan',
            Attempts: 'Attempts'
          },
          texts: {
            filter: 'Filter by Guarantor'
            //count:' '
          },
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          },
          sortIcon: {
            base: "fa",
            is: "fa-sort",
            up: "fa-sort-asc",  
            down: "fa-sort-desc"
          },
          customSorting: {
              amount: function (ascending) {
                return function (a, b) {
                  //console.log(a, b);
                  if(ascending)
                    return a.PatientAmount - b.PatientAmount;
                  else
                    return b.PatientAmount - a.PatientAmount;
                }
              },
          }    
      }

    };
  },

  methods: {
    ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
    ...mapActions('teamio', ['runQuery']),
    ...mapActions('lists',['createList']),
    ...mapMutations('teamio', ['emptyResults']),
    ...mapMutations('lists', ['setList']),

    // ---- Follow-ups redesign (new branch only). Each one hands off to the page's EXISTING models
    // ---- and methods (primaryTabIndex → its watcher, applyOutstandingClaimsFilter, the balance
    // ---- filters' patientBalancesData); none re-implements them.

    /** The "Classic view" / "New view" links: a Super Admin re-renders in place, an office user reloads (uiSwitch.js). */
    fuSetUi(target) {
      if (switchUi('aging', target, { isSuperAdmin: this.isSuperAdmin, userKey: this.userID }) === 'rerender') this.fuUiVersion += 1;
    },

    /** A sub-tab: the old b-tabs' v-model, so the page's own watcher gates and loads it. */
    fuSetTab(index) {
      if (index === this.primaryTabIndex) return;
      this.fuBucket = null;
      this.fuCounts = null;
      this.primaryTabIndex = index;
    },

    /** "Older than N days" → Apply: the old tab's Apply button (validates, then init()). */
    fuApplyOlder() {
      this.fuBucket = null;
      this.applyOutstandingClaimsFilter();
    },

    /**
     * "Over $": digits and one '.' only (anything else could only empty the table — the page
     * compares PatientAmount > this). Blank is the page's own default, 0.
     */
    fuSetBalanceOver(event) {
      const raw = String(event.target.value || '');
      let clean = raw.replace(/[^\d.]/g, '');
      const dot = clean.indexOf('.');
      if (dot !== -1) clean = clean.slice(0, dot + 1) + clean.slice(dot + 1).replace(/\./g, '');
      if (clean !== raw) event.target.value = clean;
      this.patientBalanceGreaterThan = clean === '' ? 0 : clean;
    },

    /** Billing type: the old multiselect's object from billingTypes, or null for "All". */
    fuSetBilling(name) {
      this.billingType = name ? (this.billingTypes || []).find(b => b.ItemName === name) || null : null;
    },

    // Coerce an "older than N days" filter to a clean integer string (or ''
    // when blank / non-numeric) before it goes on the wire. The backend
    // interpolates this straight into `INTERVAL <n> DAY`, so a non-numeric
    // value (e.g. a carrier name mistyped into the box) would crash the query
    // with ER_BAD_FIELD_ERROR. Mirror of the backend sql/sanitize.js helper.
    sanitizeDaysFilter(val) {
      const str = String(val === null || val === undefined ? '' : val).trim();
      return /^\d+$/.test(str) ? str : '';
    },

    async init() {
       this.busy = true;
       this.resetError();

       // Skip the default Outstanding Claims fetch when the user can't
       // view it — mounted() will redirect them to their first allowed
       // tab, whose watcher fires the appropriate query. Without this
       // check, init() would issue a wasted backend call AND briefly
       // flash claim data before the tab switch happens.
       if (!this.canViewOutstandingClaims) {
         this.busy = false;
         return;
       }

       let query = {
          type: 'AGING_OUTSTANDING_CLAIMS',
          _date: moment().format('YYYY-MM-DD'),
          siteID: this.siteID,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          preAuthOlderThan: this.sanitizeDaysFilter(this.preAuthOlderThan),
          agingOlderThan: this.sanitizeDaysFilter(this.agingOlderThan),
        };

        this.emptyResults();
        try {
          this.results = await this.runQuery(query);
        } finally {
          this.busy = false;
        }
    },

    async load(query) {
       this.busy = true;
       this.resetError();
       this.emptyResults();
       try {
         this.results = await this.runQuery(query);
         if(this.primaryTabIndex === 2) {
           if(this.results && this.results.data.length > 0 && this.results.data[1]) {
             let temp = this.results.data[1];
             let o = {
               DefNum: -1,
               ItemName: 'Show All'
             }
             temp.unshift(o);
             this.billingTypes = temp;
           }
         }
       } finally {
         this.busy = false;
       }
    },

    isDueToday(d) {
      if(d === null || d === '')
        return false;
      else
        return moment(d).isSame(moment(), 'day');
    },

    isEmpty(obj) {
      for(var key in obj) {
          if(obj.hasOwnProperty(key))
              return false;
      }
      return true;
    },

    agingChanged() {   
    },

    statusChanged(status) {
      ClientTableEvent.$emit('vue-tables.myTable.filter::ClaimStatus', status);
    },

    successCountDownChanged(count) {
        this.setDismissSuccessCountDown(count);
    },

    applyOutstandingClaimsFilter() {
      // Allow blank (= last 6 months, per the "Leave blank to see all" hint) or
      // a non-negative integer. Reject anything else (e.g. a mistyped carrier
      // name) up front instead of sending it to the backend, where it would be
      // interpolated into `INTERVAL <n> DAY` and crash the query.
      const val = String(this.agingOlderThan === null || this.agingOlderThan === undefined ? '' : this.agingOlderThan).trim();
      if(val !== '' && !/^\d+$/.test(val)) {
        this.$toast.error('Please enter a valid number of days before submitting');
        return;
      }
      this.init();
    },

    applyPreAuthFilter() {
      if(!isNaN(parseInt(this.preAuthOlderThan,10)))
        this.init();
      else {        
          this.$toast.error('Please enter a valid number of days before submitting');
          return;
      }
    },

    applyProcessedPreAuthFilter() {
      this.init();
    },

    applyPatientBalanceFilter() {
      //console.log(`Balance: ${this.patientBalanceGreaterThan} and billing type: ${this.billingType} and show negative balance:${this.showNegativeBalances}`);
      //this.$router.push({path : `${this.$route.path}/${this.$route.hash}`});
      this.init();
    }

  },

  computed: {
    ...mapState({
        _list: state => state.lists.list,
    }),

    // ---- Follow-ups redesign (new branch only) -------------------------------------------------

    /**
     * Which template branch renders. An office user → STAGE.aging and their own choice (the old page at "preview"). A Super Admin → the new page unless "Classic view" was picked.
     */
    fuUseNewUi() {
      // eslint-disable-next-line no-unused-expressions
      this.fuUiVersion; // re-read storage after the Classic / New view link
      return shouldUseNewFollowupsUi('aging', { isSuperAdmin: this.isSuperAdmin, userKey: this.userID, storage: browserStorage() });
    },

    // The tab bar: the header's permission rule; a count only for this page, whose rows are loaded.
    fuTabs() {
      const counts = {};
      if (this.dataToDisplay && this.fuCounts) counts.aging = this.fuCounts.now;
      return visibleTabs((a, s) => !!(this.$can && this.$can(a, s)), counts);
    },

    fuToday() {
      return moment().format('YYYY-MM-DD');
    },

    fuMoney() {
      const s = this.allSettings && this.allSettings.GENERAL_CURRENCY_SYMBOL;
      const symbol = s && s.PropertyValue ? s.PropertyValue : '$';
      return n => formatMoney(n, symbol);
    },

    // The queue on screen: the old tabs' index → its queues.js key.
    fuQueueKey() {
      return FU_AGING_QUEUES[this.primaryTabIndex] || FU_AGING_QUEUES[0];
    },

    // The old page's per-tab CASL gates, by the same index.
    fuTabAllowed() {
      return [this.canViewOutstandingClaims, this.canViewOutstandingPreAuths, this.canViewPatientBalances][this.primaryTabIndex] === true;
    },

    fuTabSubject() {
      return AGING_TAB_SUBJECTS[this.primaryTabIndex] || AGING_TAB_SUBJECTS[0];
    },

    /**
     * The sub-tabs this user may open (the old page's gates), each with its row count — a number
     * only on the tab whose rows are loaded (all three tabs share one result set, reloaded on switch).
     */
    fuSubTabs() {
      const defs = [
        { index: 0, key: 'outstandingClaims', label: 'Outstanding claims', allowed: this.canViewOutstandingClaims },
        { index: 1, key: 'outstandingPreAuths', label: 'Pre-authorizations', allowed: this.canViewOutstandingPreAuths },
        { index: 2, key: 'patientBalances', label: 'Patient balances', allowed: this.canViewPatientBalances },
      ];
      return defs.filter(d => d.allowed).map((d) => {
        const on = d.index === this.primaryTabIndex;
        return Object.assign({}, d, { on, count: on && !this.busy && this.dataToDisplay ? this.fuTabRows.length : null });
      });
    },

    /**
     * The tab's rows: the page's own computed for it (patientBalancesData already applies "Over $",
     * billing type and credits). Nothing while a load is running — the three computeds read the
     * same result set, which still holds the PREVIOUS tab's rows until the new ones arrive.
     */
    fuTabRows() {
      if (this.busy) return [];
      if (this.fuQueueKey === 'outstandingClaims') return this.outstandingClaimsData || [];
      if (this.fuQueueKey === 'outstandingPreAuths') return this.outstandingPreAuthData || [];
      return this.patientBalancesData || [];
    },

    // What the table shows: the tab's rows, narrowed to the age-bucket card that is on.
    fuRows() {
      if (this.fuQueueKey === 'patientBalances') return this.fuTabRows;
      return rowsInAgingBucket(this.fuTabRows, this.fuBucket, this.fuToday);
    },

    fuBucketCards() {
      return agingBucketCards(this.fuTabRows, {
        today: this.fuToday,
        kind: this.fuQueueKey === 'outstandingClaims' ? 'claims' : 'preauths',
        money: this.fuMoney,
      });
    },

    // Every guarantor the tab loaded, before the page's own filters narrow the table.
    fuBalanceCards() {
      const all = !this.busy && this.fuQueueKey === 'patientBalances' && this.dataToDisplay ? this.dataToDisplay[0] : [];
      return balanceCards(all || [], { money: this.fuMoney });
    },

    fuBalanceOverText() {
      const v = this.patientBalanceGreaterThan;
      return v === 0 || v === '0' || v === null || v === undefined ? '' : String(v);
    },

    // The old multiselect's options minus its "Show All" row (the select's own "All billing types").
    fuBillingOptions() {
      return (this.billingTypes || []).filter(b => b && b.ItemName && b.DefNum !== -1);
    },

    fuBillingValue() {
      return this.billingType && this.billingType.ItemName !== 'Show All' ? this.billingType.ItemName : '';
    },

    dataToDisplay() {
       if(this.results && this.results.data && this.results.data.length > 0)
          return this.results.data;
    },

    outstandingClaimsData() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0 && this.dataToDisplay[0]) {       
        return this.dataToDisplay[0];
      }
    },

    outstandingClaimsDataMoreThan30DaysOld() {
      if(this.outstandingClaimsData)
        return this.outstandingClaimsData.filter(a => moment(a.DateSent).isBefore(moment().subtract(30, 'days'), 'day'));
    },

    outstandingPreAuthData() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0 && this.dataToDisplay[0]) {
        return this.dataToDisplay[0];
      }
    },

    outstandingPreAuthDataMoreThan30DaysOld() {
      if(this.outstandingPreAuthData)
        return this.outstandingPreAuthData.filter(a => moment(a.DateSent).isBefore(moment().subtract(30, 'days'), 'day'));
    },

    /*
    processedPreAuthData() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0 && this.dataToDisplay[2]) {
        return this.dataToDisplay[2];
      }
    },
    */

    patientBalancesData() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0 && this.dataToDisplay[0] && this.dataToDisplay[0].length > 0) {
        let data;

        if(this.showNegativeBalances)
           data = this.dataToDisplay[0].filter(r => r.PatientAmount < 0);
        else {
          if(this.billingType !== null && this.billingType.ItemName !== 'Show All')
            data = this.dataToDisplay[0].filter(r => r.BillingType === this.billingType.ItemName).filter(r => r.PatientAmount > this.patientBalanceGreaterThan);
          else
            data = this.dataToDisplay[0].filter(r => r.PatientAmount > this.patientBalanceGreaterThan);
        }
        return data;
      }
    },

    // Per-tab access gates. Used in the template to render the data table
    // vs. <EmptyState restricted> inside each tab. Mirrors AGING_TAB_SUBJECTS.
    canViewOutstandingClaims() {
      return this.$can('view', 'outstanding_claim') || this.$can('view', 'all');
    },

    canViewOutstandingPreAuths() {
      return this.$can('view', 'outstanding_preauths') || this.$can('view', 'all');
    },

    canViewPatientBalances() {
      return this.$can('view', 'patient_balances') || this.$can('view', 'all');
    },

    // First tab the user is allowed to view. Used by mounted() to default
    // to a tab the user can actually access when no hash route is matched.
    // See cypress/node-tests/aging-tab-decision.test.js for full semantics.
    firstAllowedTabIndex() {
      if (this.canViewOutstandingClaims) return 0;
      if (this.canViewOutstandingPreAuths) return 1;
      if (this.canViewPatientBalances) return 2;
      // Defensive — the route guard should have redirected away. Returning
      // 0 keeps the UI rendering; tab 0's restricted state will show.
      return 0;
    },

  },

  watch: {
    primaryTabIndex(newValue) {
      // Skip the backend query entirely when the user lacks access to the
      // tab they switched to. The inner <EmptyState restricted> already
      // renders the lock UI, so there's nothing to load. This also avoids
      // leaking data through the runQuery Lambda (which has no auth check)
      // for tabs the user shouldn't see. See cypress/node-tests/
      // aging-tab-decision.test.js for the full decision matrix.
      const subjectByIndex = { 0: 'outstanding_claim', 1: 'outstanding_preauths', 2: 'patient_balances' };
      const subject = subjectByIndex[newValue];
      if (!subject) return;
      if (!this.$can('view', subject) && !this.$can('view', 'all')) return;

      let query = {
          _date: moment().format('YYYY-MM-DD'),
          siteID: this.siteID,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          preAuthOlderThan: this.sanitizeDaysFilter(this.preAuthOlderThan),
          agingOlderThan: this.sanitizeDaysFilter(this.agingOlderThan),
      };

      switch(newValue) {
        case 0: query['type'] = 'AGING_OUTSTANDING_CLAIMS';
                break;
        case 1: query['type'] = 'AGING_OUTSTANDING_PREAUTH';
                break;
        case 2: query['type'] = 'AGING_PATIENT_BALANCES';
                break;
      }
      this.load(query);
    }
  }

};

</script>

<style src="./Aging.scss" lang="scss" />
<!-- The redesign's page-level styles (new branch only) live with the Follow-ups pages. -->
<style src="../Followups/aging-page.scss" lang="scss" scoped />

<style scoped>
  /* Making the Attached Procedures column smaller */
  .VueTables th:nth-child(6) { width: 10%; }

  /* Making the checkbox for show negative balance in patient balance tab show up in same line */
  .custom-control {
    display: inline-flex;
  }
  .out_claims{
    background-color: #F5F8FA;
  }
  .leave_blank span{
    margin-left: -15px !important;
  }
table.VueTables__table thead tr:nth-child(1) th span{
    /* Removed margin-top: -15px to allow proper inline sort icon alignment */
    display: inline !important;
    vertical-align: middle !important;
  }

  th.VueTables__sortable span:first-child{
    display: inline !important;
    vertical-align: middle !important;
  }
  .table-responsive .VueTables__table .VueTables__sortable{
    width: auto !important;
    white-space: nowrap !important;
  } 

  .table-responsive .VueTables__table tr th{
    width: auto !important;
    min-width: 80px !important;
    padding-top: 12px !important;
    padding-bottom: 10px !important;
    background-color: #e0e0e0 !important;
    font-size: 12px;
  }
.table-responsive .VueTables__table thead tr th span{
    display: inline !important;
    vertical-align: middle !important;
}
.glyphicon-sort:before, .glyphicon-chevron-up:before, .glyphicon-chevron-down:before{

}

@media (max-width:821px) {
  .unsch_appo{
    width: 100%;
  }
  .apply_f{
    margin-left: 50% !important;
  }  
  .date_p{
    width: 75%
  }  
  .pat_bal_inp{
   width: 100% !important
  }
}
@media (min-width:822px) {
  .unsch_appo{
    width: 600px
  }
  .date_p{
    width: 50%
  }
  .pat_bal_inp{
   width: 50% !important
  }
  .custom-switch .custom-control-label::before{
    margin-top: 5px;
  }
    .custom-switch .custom-control-label::after{
    margin-top: 5px;
  }
}
@media (min-width:1200px) {
  .date_p{
    width: 25%
  }
  .pat_bal_inp{
   width: 25% !important
  }
}

</style>
